import { z } from "zod";

/**
 * Knowledge folders (openspec change `knowledge-folders`): organisation only. A project has one
 * folder tree for its files and one for its wiki; a folder holds a name and a parent, never
 * readers, a classification or defaults, so moving an item changes no access (ADR 0006
 * amendment). Items without a folder sit at the root of their section.
 */

export const folderIdSchema = z.uuid().brand<"FolderId">();
export type FolderId = z.infer<typeof folderIdSchema>;

/** `wiki` holds wiki pages; `files` holds every other kind of document. */
export const folderSectionSchema = z.enum(["files", "wiki"]);
export type FolderSection = z.infer<typeof folderSectionSchema>;

/** Most characters (code points, after NFC) in a folder name. */
export const MAX_FOLDER_NAME_CHARS = 120;

/** Most folders per section of a project (a bound for the tree UI and the depth queries). */
export const MAX_FOLDERS_PER_SECTION = 2000;

/** Deepest nesting: a root folder is level 1, and items may sit in a level-5 folder. */
export const MAX_FOLDER_DEPTH = 5;

/** NFC, then trimmed: the form a name is checked, compared and stored in. */
export function normalizeFolderName(raw: string): string {
  return raw.normalize("NFC").trim();
}

// Control and format characters (zero-width, joiners, direction marks, overrides, isolates),
// line and paragraph separators: a name must read as it is stored, and two names that look
// alike must not both pass the uniqueness check.
const FORBIDDEN_IN_NAME = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}/\\]/u;

/** Why a normalized name is refused, or null when it is valid (design §2). */
export function folderNameProblem(name: string): "empty" | "too_long" | "invalid" | null {
  const length = Array.from(name).length;
  if (length === 0) {
    return "empty";
  }
  if (length > MAX_FOLDER_NAME_CHARS) {
    return "too_long";
  }
  if (name === "." || name === ".." || FORBIDDEN_IN_NAME.test(name)) {
    return "invalid";
  }
  return null;
}

/**
 * A folder name as sent by a client: normalized (NFC, trimmed), 1 to 120 characters, without
 * control characters, `/` or `\`, and not `.` or `..`. Names are user content: they are stored
 * as text and never used in a path or a storage key.
 */
export const folderNameSchema = z
  .string()
  .max(4 * MAX_FOLDER_NAME_CHARS)
  .transform(normalizeFolderName)
  .refine((name) => folderNameProblem(name) === null, {
    message: `A folder name has 1 to ${MAX_FOLDER_NAME_CHARS} characters, without control characters, / or \\, and is not . or ..`,
  });

/** The key a name is unique by within its parent (ignoring case). */
export function folderNameKey(name: string): string {
  return normalizeFolderName(name).toLowerCase();
}

export const folderSchema = z.object({
  id: folderIdSchema,
  section: folderSectionSchema,
  /** Null for a folder at the root of its section. */
  parentId: folderIdSchema.nullable(),
  name: z.string(),
});
export type Folder = z.infer<typeof folderSchema>;

/**
 * `GET /api/projects/:projectId/folders`: every folder of the project for its members, only the
 * folders on the path of an item they can read for a guest. Never a count of items.
 */
export const folderListSchema = z.object({ folders: z.array(folderSchema) });
export type FolderList = z.infer<typeof folderListSchema>;

export const createFolderRequestSchema = z.object({
  section: folderSectionSchema,
  /** The parent folder (same section); the section root when absent or null. */
  parentId: folderIdSchema.nullable().optional(),
  name: folderNameSchema,
});
export type CreateFolderRequest = z.infer<typeof createFolderRequestSchema>;

export const renameFolderRequestSchema = z.object({ name: folderNameSchema });
export type RenameFolderRequest = z.infer<typeof renameFolderRequestSchema>;

export const moveFolderRequestSchema = z.object({
  /** The new parent (same section), or null for the section root. */
  parentId: folderIdSchema.nullable(),
});
export type MoveFolderRequest = z.infer<typeof moveFolderRequestSchema>;

/**
 * What deleting a folder moved to its parent: its subfolders, its documents (live or deleted,
 * readable by the caller or not) and its drafts, and the subfolders renamed with a numbered
 * suffix on a name conflict. Nothing is deleted but the folder itself.
 */
export const deleteFolderResultSchema = z.object({
  movedFolders: z.int().min(0),
  movedItems: z.int().min(0),
  movedDrafts: z.int().min(0),
  renamed: z.array(folderIdSchema),
});
export type DeleteFolderResult = z.infer<typeof deleteFolderResultSchema>;

/** `PUT /api/projects/:projectId/documents/:documentId/folder`: a file or a wiki page. */
export const moveItemRequestSchema = z.object({ folderId: folderIdSchema.nullable() });
export type MoveItemRequest = z.infer<typeof moveItemRequestSchema>;

/** The answer to an item move: where the document now sits (null: the section root). */
export const movedItemSchema = z.object({
  documentId: z.string(),
  folderId: folderIdSchema.nullable(),
});
export type MovedItem = z.infer<typeof movedItemSchema>;

/**
 * The outcome codes of a refused folder operation (audit `folder.refused`). Over HTTP:
 * `409 name_taken`, `409 folder_limit`, `409 project_frozen`; `422 too_deep`, `cycle` and
 * `wrong_section`.
 */
export const FOLDER_REFUSALS = [
  "name_taken",
  "folder_limit",
  "too_deep",
  "cycle",
  "wrong_section",
  "frozen",
  // mcp-foundations-files: an MCP `delete_folder` on a folder that is not strictly empty.
  "not_empty",
] as const;
export type FolderRefusal = (typeof FOLDER_REFUSALS)[number];

/** Optional on every request that can name a folder: absent or null is the section root. */
export const optionalFolderIdSchema = folderIdSchema.nullable().optional();
